<template>
  <div style="width: 100%" class="d-flex justify-center items-center">
    <form class="form-payment-information mb-60" :style="{ width: width }">
      <div class="col-sm-12 mb-20">
        <label class="">Card Holder Name</label>
        <input
          type="text"
          class="form-control alphabets-only"
          placeholder="Enter card holder name"
          v-model="cardHolderName"
          autocomplete="off"
        />
      </div>
      <div class="col-sm-12 mb-20">
        <label class="required" for="card_number">Card Number</label>
        <div id="card_number" class="form-control"></div>
      </div>
      <div style="width: 100%" class="mb-20">
        <div class="col-sm-6">
          <label class="required" for="expiration_date">Expiration Date</label>
          <div id="expiration_date" class="form-control"></div>
        </div>
        <div class="col-sm-6">
          <label class="required" for="security_code">CVC</label>
          <div id="security_code" class="form-control"></div>
        </div>
      </div>
      <div class="col-sm-12 mb-20">
        <label class="required">Card Type</label>
        <div class="d-flex">
          <label
            class="cursor-pointer"
            v-for="(item, index) in cardType"
            :key="index"
            @click="selectedCardType = item.value"
            :class="{ 'pl-20': index > 0 }"
          >
            <input
              type="radio"
              class="control-radio"
              :value="item.value"
              :checked="selectedCardType === item.value"
              v-model="selectedCardType"
            />
            <span class="pl-5 font-weight-normal v-align-middle">
              {{ item.text }}
            </span>
          </label>
        </div>
      </div>

      <div class="col-sm-12 mb-10">
        <button
          type="button"
          id="btn-payment-information"
          class="btn btn-primary"
          @click="handleSubmit"
        >
          {{ nameBtn }}
        </button>
      </div>

      <div class="col-sm-12 mb-20" v-if="showTitle">
        <div class="instructional-text-wrapper" style="margin-top: 20px">
          <div id="title">Secure Payment Information</div>
          <p style="line-height: 1rem; margin-top: 10px">
            We take the protection of your financial information seriously. All
            credit card transactions conducted on this page are encrypted using
            industry-standard 256-bit Secure Socket Layer (SSL) technology to
            ensure confidentiality and integrity of your data.
          </p>

          <p style="line-height: 1rem; margin-top: 15px">
            Our payment systems are PCI DSS (Payment Card Industry Data Security
            Standard) compliant, which means we adhere to globally recognised
            requirements for safeguarding cardholder information. Please note
            that your full credit card details are never stored on our servers;
            all transactions are securely processed through our accredited
            payment gateway partners.
          </p>

          <p style="line-height: 1rem; margin-top: 15px">
            Should you require further information regarding our payment
            security measures, please contact us at
            <a href="mailto:support@trransfer.com">support@trransfer.com</a>.
          </p>
        </div>
      </div>
    </form>

    <!-- Start: BlockUI -->
    <loader :content="msg" :visible="isLoading" />
    <!-- End: BlockUI -->
  </div>
</template>
<script>
import customerService from "@/services/master/customer/customerService.js";
import { init, createElement } from "@airwallex/components-sdk";

export default {
  props: {
    width: {
      type: String,
      default: "40%",
    },
    nameBtn: {
      type: String,
      default: "Save Card",
    },
    showTitle: {
      type: Boolean,
      default: true,
    },
    awClientSecret: {
      type: Object,
      default: {},
    },
    urlSearchParams: {
      type: Object,
      default: () => ({}),
    },
    customerId: {
      type: String,
      default: "",
    },
    paymentPage: {
      type: Boolean,
      default: false,
    },
    invoiceNumber: {
      type: String,
      default: "",
    },
    invoiceType: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      selectedCardType: "Business",
      cardHolderName: "",
      cardType: [
        { value: "Business", text: "Business" },
        { value: "Personal", text: "Personal" },
      ],
      cardNumber: null,
      expirationDate: null,
      cvc: null,
      cardNumberMsg: null,
      expirationDateMsg: null,
      cvcMsg: null,
      statusConfirmation: false,
      isLoading: false,
      msg: "Please wait",
    };
  },
  methods: {
    async resetAirwallex() {
      if (this.cardNumber) this.cardNumber.unmount();
      if (this.expirationDate) this.expirationDate.unmount();
      if (this.cvc) this.cvc.unmount();
      this.cardHolderName = "";
      this.cardNumberMsg = null;
      this.expirationDateMsg = null;
      this.cvcMsg = null;
      await this.initAirwallex();
    },
    async initAirwallex() {
      const hostname = window.location.hostname;
      await init({
        env: hostname === "app.trransfer.com" ? "prod" : "demo",
        enabledElements: ["payments"],
      });

      this.cardNumber = await createElement("cardNumber");
      this.expirationDate = await createElement("expiry");
      this.cvc = await createElement("cvc");

      this.cardNumber.mount("card_number");
      this.expirationDate.mount("expiration_date");
      this.cvc.mount("security_code");

      this.cardNumber.on("change", (event) => {
        if (event.detail && event.detail.error) {
          this.cardNumberMsg = event.detail.error.message;
        } else {
          this.cardNumberMsg = null;
        }
      });

      this.expirationDate.on("change", (event) => {
        if (event.detail && event.detail.error) {
          this.expirationDateMsg = event.detail.error.message;
        } else {
          this.expirationDateMsg = null;
        }
      });

      this.cvc.on("change", (event) => {
        if (event.detail && event.detail.error) {
          this.cvcMsg = event.detail.error.message;
        } else {
          this.cvcMsg = null;
        }
      });
    },
    async handleSubmit() {
      if (this.cardNumberMsg) {
        this.$notification.error(`${this.cardNumberMsg}`, {
          title: "Error",
        });

        return;
      }

      if (this.expirationDateMsg) {
        this.$notification.error(`${this.expirationDateMsg}`, {
          title: "Error",
        });

        return;
      }

      if (this.cvcMsg) {
        this.$notification.error(`${this.cvcMsg}`, {
          title: "Error",
        });

        return;
      }

      if (this.invoiceNumber && this.invoiceType === "Monthly") {
        this.$emit("confirmation");
      } else {
        this.processingPayment();
      }
    },
    async processingPayment() {
      if (this.paymentPage) {
        if (!this.statusConfirmation) {
          this.$emit("confirmationSubmit");
          this.statusConfirmation = true;
          return;
        }
        this.$eventBus.$emit("loading", {
          msg: "Payment in progress, please do not refresh or close this browser.",
          isLoading: true,
        });
      } else {
        this.isLoading = true;
      }
      try {
        const response = await this.cardNumber.createPaymentConsent({
          customer_id: this.awClientSecret.airwallexCustomerId,
          client_secret: this.awClientSecret.clientSecret,
          currency: "SGD",
          next_triggered_by: "merchant",
        });

        if (response) {
          const payload = {
            customerId: this.customerId,
            last4: response.payment_method.last4,
          };

          const verifyPaymentMethod = await customerService.verifyPaymentMethod(
            payload
          );

          if (verifyPaymentMethod.isSuccess) {
            await this.attachPaymentMethod(response);
          }
        }
      } catch (error) {
        if (this.paymentPage) {
          this.$eventBus.$emit("loading", {
            msg: "Please wait",
            isLoading: false,
          });
        }
        if (error && error.errors) {
          for (let index = 0; index < error.errors.length; index++) {
            this.$notification.error(error.errors[index], { title: "Error" });
          }
        } else {
          this.$notification.error(error.message, { title: "Error" });
        }
      } finally {
        this.isLoading = false;
      }
    },
    async attachPaymentMethod(paymentConsentResponse) {
      try {
        const payload = {
          refCustomerId: paymentConsentResponse.customer_id,
          refPaymentConsentId: paymentConsentResponse.payment_consent_id,
          refPaymentMethodId: paymentConsentResponse.payment_method.id,
          paymentCardType: this.selectedCardType,
          cardHolderName: this.cardHolderName,
        };

        const response = await customerService.attachPaymentMethod(payload);
        if (response.isSuccess) {
          this.$notification.success("Card successfully saved.", {
            title: "Success",
          });
          const paymentMethodId = response.data.id;
          this.$emit("submit", paymentMethodId);
        }
      } catch (error) {
        if (this.paymentPage) {
          this.$eventBus.$emit("loading", {
            msg: "Please wait",
            isLoading: false,
          });
        }
        if (error.errors) {
          for (let index = 0; index < error.errors.length; index++) {
            this.$notification.error(error.errors[index], { title: "Error" });
          }
        }
      }
    },
  },
};
</script>

<style scoped>
.form-payment-information {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
}

.form-payment-information h2 {
  font-weight: 700;
}

#btn-payment-information.btn-primary {
  width: 100% !important;
  padding: 10px;
}

label {
  color: #777 !important;
  display: block !important;
  font-size: 0.8667rem !important;
}

.instructional-text-wrapper #title {
  font-size: 16px;
  font-weight: 700;
}

.instructional-text-wrapper p {
  font-size: 0.8667rem;
  text-align: justify;
}
</style>


// WEBPACK FOOTER //
// src/components/shared/FormCard.vue